/**
 * Storefront analytics.
 *
 * There was none. Not a light setup — none: `gtag`, `dataLayer`, `track(`,
 * `fbq` returned zero hits across `frontend/src`, so nothing was known about
 * which sections earn a click, where the basket is abandoned, or whether
 * Try-On sells anything. Every optimisation decision was a guess.
 *
 * The substrate is `window.dataLayer`, because that is what GTM, GA4 and the
 * Meta pixel all consume — the vendor choice stays a tag-manager concern rather
 * than something baked into 30 call sites. When `REACT_APP_GTM_ID` or
 * `REACT_APP_GA_ID` is present the loader in `mountAnalytics()` attaches the
 * real script; without one the events still queue on `dataLayer`, so the
 * instrumentation is testable — and verifiable in a browser console — before
 * any account exists.
 *
 * Event names follow GA4's ecommerce spec rather than an invented vocabulary,
 * so a GA4 property understands the payloads with no mapping layer.
 */

const GTM_ID = process.env.REACT_APP_GTM_ID || "";
const GA_ID = process.env.REACT_APP_GA_ID || "";
const DEBUG = process.env.NODE_ENV !== "production";

/* Meta (Facebook/Instagram) pixel. The id is public — it ships in every page
   that loads the pixel — so it lives here rather than only in build env, and
   `REACT_APP_META_PIXEL_ID` can still override it. Production builds only:
   dev servers and tests must not feed fake visits into the ad account. */
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID ?? "2522878224770036";
const META_ENABLED = Boolean(META_PIXEL_ID) && process.env.NODE_ENV === "production";

/** The currency every price in this storefront is denominated in. */
const CURRENCY = "AZN";

function dataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Minor units to major.
 *
 * Prices travel as integers throughout the platform (qəpik), and GA4 expects a
 * decimal. Sending 89200 instead of 892 would not error anywhere — it would
 * just silently report revenue a hundred times too high.
 */
export function toMajor(minor) {
  const n = Number(minor);
  return Number.isFinite(n) ? Math.round(n) / 100 : undefined;
}

/** One catalogue row in GA4's `items` shape. */
export function toItem(product, extra = {}) {
  if (!product) return null;
  const title = typeof product.title === "string"
    ? product.title
    : (product.title?.az || product.title?.en || product.displayTitle || product.handle);
  return {
    item_id: product.handle || product.id,
    item_name: title,
    item_brand: "Atelier Di Falco",
    item_category: product.category_label || product.category || undefined,
    price: toMajor(product.price ?? product.min_price ?? product.displayPrice),
    currency: CURRENCY,
    ...extra,
  };
}

/**
 * Push one event.
 *
 * Never throws: a broken analytics call must not take a page down with it, and
 * the whole point of adding tracking is that it is invisible when it fails.
 */
export function track(event, params = {}) {
  try {
    const layer = dataLayer();
    if (!layer) return;
    layer.push({ event, ...params });
    if (DEBUG) console.debug("[analytics]", event, params);
  } catch (error) {
    if (DEBUG) console.warn("[analytics] dropped", event, error);
  }
}

/**
 * Send one Meta pixel event.
 *
 * The pixel reports the page URL with every event, and the guest order pages
 * carry the shopper's e-mail or phone in the query string
 * (`/checkout/success?order_id=…&email=…`). Meta flags — and can restrict —
 * pixels that receive personal data that way, so on such a URL nothing is sent.
 */
function pixel(kind, name, params, options) {
  try {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    if (/[?&](email|phone)=/i.test(window.location.search)) return;
    if (options) window.fbq(kind, name, params || {}, options);
    else if (params) window.fbq(kind, name, params);
    else window.fbq(kind, name);
  } catch (error) {
    if (DEBUG) console.warn("[analytics] pixel dropped", name, error);
  }
}

/** GA4 `items` → the fields Meta's standard events understand. */
function metaContents(items) {
  const list = (items || []).filter(Boolean);
  return {
    content_type: "product",
    content_ids: list.map((i) => String(i.item_id)),
    contents: list.map((i) => ({ id: String(i.item_id), quantity: i.quantity || 1, item_price: i.price })),
    num_items: list.reduce((n, i) => n + (i.quantity || 1), 0),
  };
}

/** GA4 ecommerce events want their payload under `ecommerce`. */
export function trackEcommerce(event, ecommerce) {
  track(event, { ecommerce: { currency: CURRENCY, ...ecommerce } });
}

/* ------------------------------------------------------------------ *
 * The catalogue funnel. One function per step, so a call site reads as
 * the thing that happened rather than as a string literal someone has to
 * keep spelled correctly.
 * ------------------------------------------------------------------ */

let lastPixelPath = null;

export const analytics = {
  pageView(path, title) {
    track("page_view", { page_path: path, page_title: title });
    if (typeof window === "undefined") return;
    // Meta counts pages, not query strings: picking a colour on a PDP rewrites
    // `?colour=` and would otherwise log a fresh PageView per swatch.
    const pathname = window.location.pathname;
    const samePage = pathname === lastPixelPath;
    lastPixelPath = pathname;
    if (samePage) return;
    // The landing page's PageView was already sent by the snippet in
    // public/index.html; only the in-app navigations after it go from here.
    if (window.__adfPixelLandingSent) {
      window.__adfPixelLandingSent = false;
      return;
    }
    pixel("track", "PageView");
  },

  /** `category` marks a real listing page (not a homepage rail), which is
   *  what Meta's ViewCategory — and category retargeting — should see. */
  viewItemList(listName, products, { category = false } = {}) {
    const items = (products || []).map((p, index) => toItem(p, { index })).filter(Boolean);
    if (!items.length) return;
    trackEcommerce("view_item_list", { item_list_name: listName, items });
    if (category) {
      const { content_ids, content_type } = metaContents(items.slice(0, 20));
      const where = typeof window !== "undefined" ? window.location.pathname : listName;
      pixel("trackCustom", "ViewCategory", { content_category: where, content_ids, content_type });
    }
  },

  selectItem(listName, product, index) {
    const item = toItem(product, { index });
    if (!item) return;
    trackEcommerce("select_item", { item_list_name: listName, items: [item] });
  },

  viewItem(product) {
    const item = toItem(product);
    if (!item) return;
    trackEcommerce("view_item", { value: item.price, items: [item] });
    pixel("track", "ViewContent", { ...metaContents([item]), content_name: item.item_name, value: item.price, currency: CURRENCY });
  },

  addToCart(product, { variantTitle, quantity = 1 } = {}) {
    const item = toItem(product, { item_variant: variantTitle, quantity });
    if (!item) return;
    trackEcommerce("add_to_cart", { value: item.price, items: [item] });
    pixel("track", "AddToCart", { ...metaContents([item]), content_name: item.item_name, value: item.price, currency: CURRENCY });
  },

  addToWishlist(product) {
    const item = toItem(product);
    if (!item) return;
    trackEcommerce("add_to_wishlist", { value: item.price, items: [item] });
    pixel("track", "AddToWishlist", { ...metaContents([item]), content_name: item.item_name, value: item.price, currency: CURRENCY });
  },

  selectSize(product, size, { source = "pdp" } = {}) {
    track("select_size", { item_id: product?.handle, size, source });
    if (product?.handle) {
      pixel("track", "CustomizeProduct", { content_ids: [String(product.handle)], content_type: "product", size });
    }
  },

  openSizeGuide(product) {
    track("size_guide_open", { item_id: product?.handle });
  },

  /** The differentiator gets its own funnel — it is the reason to build this. */
  tryOn: {
    open(product, source) { track("try_on_open", { item_id: product?.handle, source }); },
    photoRejected(reason) { track("try_on_photo_rejected", { reason }); },
    submit(product, mode) {
      track("try_on_submit", { item_id: product?.handle, photo_source: mode });
      pixel("trackCustom", "TryOn", {
        content_ids: product?.handle ? [String(product.handle)] : [],
        content_type: "product",
        photo_source: mode,
      });
    },
    success(product, provider, isFallback) {
      track("try_on_success", { item_id: product?.handle, provider, is_fallback: Boolean(isFallback) });
    },
    failed(product, code) { track("try_on_failed", { item_id: product?.handle, error_code: code }); },
    shared(product) { track("try_on_shared", { item_id: product?.handle }); },
    downloaded(product) { track("try_on_downloaded", { item_id: product?.handle }); },
  },

  completeTheLook(product, count) {
    track("complete_the_look_add", { item_id: product?.handle, companion_count: count });
  },

  conciergeClick(product, channel) {
    track("concierge_click", { item_id: product?.handle, channel });
  },

  search(term) {
    const q = String(term || "").trim();
    if (!q) return;
    track("search", { search_term: q });
    pixel("track", "Search", { search_string: q });
  },

  /** A written request reached the atelier: newsletter, concierge, contact
   *  form, a reserved piece. `kind` tells them apart in Events Manager. */
  lead(kind, product) {
    track("generate_lead", { lead_type: kind, item_id: product?.handle });
    pixel("track", "Lead", {
      content_category: kind,
      ...(product?.handle ? { content_ids: [String(product.handle)], content_type: "product" } : {}),
    });
  },

  /** A boutique / styling appointment was booked. */
  schedule(purpose) {
    track("schedule_appointment", { purpose });
    pixel("track", "Schedule", { content_category: purpose || "appointment" });
  },

  signUp(method = "email") {
    track("sign_up", { method });
    pixel("track", "CompleteRegistration", { status: true, content_name: method });
  },

  /** Phone, WhatsApp or e-mail link — see `trackContactClicks`. */
  contact(channel) {
    track("contact_click", { channel });
    pixel("track", "Contact", { content_category: channel });
  },

  addPaymentInfo(items, value, provider) {
    trackEcommerce("add_payment_info", { value: toMajor(value), payment_type: provider, items: items || [] });
    pixel("track", "AddPaymentInfo", { ...metaContents(items), value: toMajor(value), currency: CURRENCY });
  },

  beginCheckout(items, value) {
    trackEcommerce("begin_checkout", { value: toMajor(value), items: items || [] });
    pixel("track", "InitiateCheckout", { ...metaContents(items), value: toMajor(value), currency: CURRENCY });
  },

  /**
   * The order exists. Fired when the order is placed, before any redirect to a
   * card gateway — the shopper may never come back to a page of ours, and the
   * success page itself carries their contact details in its URL.
   */
  purchase(order) {
    if (!order?.id) return;
    const items = (order.items || []).map((line) => ({
      item_id: line.product_handle || line.product_id,
      item_name: line.title_snapshot?.az || line.title_snapshot?.en || line.sku,
      item_brand: "Atelier Di Falco",
      price: toMajor(line.unit_price),
      quantity: line.quantity,
      currency: CURRENCY,
    }));
    const value = toMajor(order.total);
    trackEcommerce("purchase", {
      transaction_id: String(order.display_id || order.id),
      value,
      shipping: toMajor(order.shipping_total),
      tax: toMajor(order.tax_total),
      items,
    });
    // eventID lets a future server-side (Conversions API) send be deduplicated.
    pixel("track", "Purchase", { ...metaContents(items), value, currency: CURRENCY }, { eventID: `order-${order.id}` });
  },
};

/**
 * Attach the vendor script, once, if one is configured.
 *
 * Called from `App`. Absent an id this is a no-op and the `dataLayer` queue
 * simply accumulates — which is exactly what you want in development and in
 * the tests.
 */
let mounted = false;

/**
 * Fallback loader. In production the snippet in `public/index.html` has
 * already defined `fbq`, so this returns at once; it only matters for a build
 * whose HTML lacks the snippet. `PageView` is not fired here — the route
 * tracker sends it.
 */
function mountMetaPixel() {
  if (!META_ENABLED || window.fbq) return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
}

/**
 * Contact taps, wherever the link lives — footer, contact page, PDP, store
 * cards. One delegated listener instead of an onClick on every `tel:` link, so
 * a contact link added next month is counted without anyone remembering to.
 */
function contactChannel(href) {
  if (/^tel:/i.test(href)) return "phone";
  if (/^mailto:/i.test(href)) return "email";
  if (/(^|\/\/)(wa\.me|api\.whatsapp\.com)\//i.test(href)) return "whatsapp";
  return null;
}

function trackContactClicks() {
  document.addEventListener("click", (event) => {
    const link = event.target?.closest?.("a[href]");
    const channel = link && contactChannel(link.getAttribute("href") || "");
    if (channel) analytics.contact(channel);
  }, { capture: true });
}

export function mountAnalytics() {
  if (mounted || typeof document === "undefined") return;
  mounted = true;

  mountMetaPixel();
  trackContactClicks();

  if (GTM_ID) {
    dataLayer()?.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
    document.head.appendChild(script);
    return;
  }

  if (GA_ID) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
    document.head.appendChild(script);
    // gtag reads the same array, so events pushed before this point are not lost.
    window.gtag = function gtag() { dataLayer()?.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { send_page_view: false });
  }
}

export default analytics;
